import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import Service from '../../../services/index';
import { baseUrlAdmin } from '../../../store/apiConstants';
import * as XLSX from 'xlsx';
import Authorizer from '../../session/authorizer';
import { PERMISSIONS } from '../../../session/authorize';
import './style.scss';

class TransactionHistoryTable extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();
        this.state = {
            uploadHistory: [],
            historyLoading: false
        };
    }

    componentDidMount() {
        this.loadTransactionHistory();
    }

    componentDidUpdate(prevProps) {
        if (this.props.orgId && this.props.orgId !== prevProps.orgId) {
            this.loadTransactionHistory();
        }
    }

    loadTransactionHistory = async () => {
        try {
            this.setState({ historyLoading: true });

            const orgId = this.props.orgId || localStorage.getItem('orgId');

            if (!orgId) {
                                this.setState({ historyLoading: false });
                return;
            }

            const params = new URLSearchParams();
            params.append('orgId', orgId);
            params.append('page', '1');
            params.append('limit', '100');

            const url = `${baseUrlAdmin}/finance-upload/transaction-history?${params.toString()}`;
            const response = await this.service.get(url, true);

                        
            let transactions = [];

            // Handle different response structures
            if (response?.res?.data) {
                // Case 1: Wrapped response { status: true, res: { data: { success: true, data: { transactions: [...] } } } }
                const data = response.res.data;
                
                if (data.success && data.data && Array.isArray(data.data.transactions)) {
                    transactions = data.data.transactions;
                } else if (Array.isArray(data.transactions)) {
                    transactions = data.transactions;
                } else if (data.data && Array.isArray(data.data)) {
                    transactions = data.data;
                }
            } else if (response?.data) {
                // Case 2: Direct response { success: true, data: { transactions: [...] } }
                                if (Array.isArray(response.data.transactions)) {
                    transactions = response.data.transactions;
                }
            }

                        
            this.setState({
                uploadHistory: transactions,
                historyLoading: false
            });
        } catch (error) {
            console.error('Error loading transaction history:', error);
            this.setState({
                uploadHistory: [],
                historyLoading: false
            });
            if (this.props.showToast !== false) {
                this.showToast('error', 'Error', 'Failed to load transaction history');
            }
        }
    };

    showToast = (severity, summary, detail) => {
        if (this.toast.current) {
            this.toast.current.show({
                severity: severity,
                summary: summary,
                detail: detail,
                life: 3000
            });
        }
    };

    downloadErrorSheet = async (rowData) => {
        try {
            if (!rowData._id) {
                this.showToast('error', 'Error', 'Transaction ID not found');
                return;
            }

            this.showToast('info', 'Download', 'Fetching transaction details...');

            const url = `${baseUrlAdmin}/finance-upload/transaction/${rowData._id}`;
            const response = await this.service.get(url, true);

                        
            let transaction = null;

            // Handle different response structures
            if (response?.res?.data) {
                const data = response.res.data;
                if (data.success && data.data) {
                    transaction = data.data;
                } else if (data._id) {
                    transaction = data;
                }
            } else if (response?.data) {
                if (response.data.success && response.data.data) {
                    transaction = response.data.data;
                } else if (response.data._id) {
                    transaction = response.data;
                }
            }

            if (!transaction) {
                console.error('Could not extract transaction from response');
                this.showToast('error', 'Error', 'Failed to fetch transaction details');
                return;
            }

                                                            
            let csvRows = transaction.originalCSVData;
            let headers = transaction.csvHeaders;

            // FALLBACK: If originalCSVData is missing, reconstruct from error records
            if (!csvRows || !headers || csvRows.length === 0) {
                console.warn('⚠️ Original CSV data missing, attempting to reconstruct from error records...');
                
                if (!transaction.errors || transaction.errors.length === 0) {
                    console.error('❌ No error records available for reconstruction');
                    this.showToast('error', 'Error', 'No data available to generate error sheet. The transaction may be corrupted.');
                    return;
                }

                // Reconstruct CSV data from error records
                csvRows = transaction.errors
                    .filter(err => err.record && typeof err.record === 'object')
                    .map(err => err.record);

                if (csvRows.length === 0) {
                    console.error('❌ Error records do not contain row data');
                    this.showToast('error', 'Error', 'Cannot reconstruct data from error records. The records may be incomplete.');
                    return;
                }

                // Extract headers from the first reconstructed row
                const firstRow = csvRows[0];
                headers = Object.keys(firstRow).filter(key => !key.startsWith('_')); // Filter out internal fields like _originalRow

                                                
                this.showToast('info', 'Note', 'CSV data reconstructed from error records. Some original formatting may be lost.');
            }

            if (!Array.isArray(csvRows) || csvRows.length === 0) {
                this.showToast('error', 'Error', 'No data rows found in the transaction');
                return;
            }

            // Build error map - handle multiple possible field names for admission number
            const errorMap = {};
            (transaction.errors || []).forEach(err => {
                // Try different possible admission number fields (original or sanitized)
                const admissionNo = err.AdmissionNo || err.admission_no || err.Admission_No || 
                                   err.enrollment_No || err['enrollment No'] || 
                                   err.enrollmentNo || err.enrollment_no || '';
                if (admissionNo) {
                    const errorMsg = err.error || err.errors?.join(', ') || 'Unknown error';
                    errorMap[admissionNo] = errorMsg;
                                    }
            });
            
            
            // Prepare data for Excel
            const errorHeader = 'Error_Reason';
            
            // If headers are missing, extract from first row
            if (!headers || headers.length === 0) {
                                if (csvRows && csvRows.length > 0) {
                    headers = Object.keys(csvRows[0]).filter(key => !key.startsWith('_'));
                                    }
            }
            
            const finalHeaders = [errorHeader, ...headers];

            const dataForSheet = csvRows.map(row => {
                // Try different possible admission number fields (sanitized or original)
                const admissionNo = row['AdmissionNo'] || row['admission_no'] || row['Admission_No'] ||
                                   row['enrollment_No'] || row['enrollment_no'] || row['enrollment No'] ||
                                   row['enrollmentNo'] || row['UserId'] || '';
                const errorReason = errorMap[admissionNo] || '';

                // Create the Excel row with error first
                const excelRow = {
                    [errorHeader]: errorReason,
                    ...row
                };
                
                // Remove internal fields that shouldn't be in the export
                delete excelRow._originalRow;
                delete excelRow._id;
                
                return excelRow;
            });

                                    
            const worksheet = XLSX.utils.json_to_sheet(dataForSheet, { header: finalHeaders });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');

            const fileName = `BulkUpload_Errors_${new Date(transaction.uploadDate).toISOString().split('T')[0]}.xlsx`;
                        XLSX.writeFile(workbook, fileName);

            this.showToast('success', 'Success', 'Error sheet downloaded successfully');
        } catch (error) {
            console.error('Download error sheet failed:', error);
            console.error('Error stack:', error.stack);
            this.showToast('error', 'Download Failed', `Failed to generate error sheet: ${error.message}`);
        }
    };

    render() {
        const { uploadHistory, historyLoading } = this.state;
        const { showCard = true, title = "Upload Transaction History" } = this.props;

        const totalRecords = uploadHistory ? uploadHistory.length : 0;

        const tableContent = (
            <>
                {historyLoading ? (
                    <div className="p-text-center p-p-3">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading transaction history...</p>
                    </div>
                ) : (
                    <>
                        {uploadHistory && uploadHistory.length === 0 && (
                            <div className="p-mb-3">
                                <Message severity="info" text="No upload transactions found. Upload a file to see history here." />
                            </div>
                        )}
                        <DataTable
                            value={uploadHistory}
                            paginator
                            rows={10}
                            className="p-datatable-sm"
                            emptyMessage="No transactions found"
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                        >
                            <Column
                                field="uploadDate"
                                header="Date & Time"
                                body={(rowData) => {
                                    try {
                                        return new Date(rowData.uploadDate).toLocaleString();
                                    } catch (e) {
                                        return rowData.uploadDate;
                                    }
                                }}
                                style={{ width: '180px' }}
                                sortable
                            />
                            <Column
                                field="uploadedByName"
                                header="Uploaded By"
                                body={(rowData) => rowData.uploadedByName || rowData.uploadedBy || 'System'}
                                style={{ width: '150px' }}
                            />
                            <Column
                                field="recordCount"
                                header="Records"
                                style={{ width: '100px' }}
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={(rowData) => (
                                    <span style={{
                                        color: rowData.status === 'Success' ? 'green' :
                                            (rowData.status === 'In Progress' ? 'orange' : 'red'),
                                        fontWeight: 'bold'
                                    }}>
                                        {rowData.status}
                                    </span>
                                )}
                                style={{ width: '100px' }}
                                sortable
                            />
                            <Column
                                header="Details"
                                body={(rowData) => {
                                    if (rowData.status === 'Success' || rowData.status === 'Partial Success') {
                                        return (
                                            <div>
                                                <div>✓ Success: {rowData.successCount || 0}</div>
                                                <div>✗ Failed: {rowData.failedCount || 0}</div>
                                                <div>📄 Receipts: {rowData.receiptsCreated || 0}</div>
                                            </div>
                                        );
                                    } else if (rowData.status === 'In Progress') {
                                        return <div>Processing...</div>;
                                    } else {
                                        return (
                                            <div>
                                                <div style={{ color: 'red' }}>✗ Errors: {rowData.errorCount || rowData.failedCount || 0}</div>
                                            </div>
                                        );
                                    }
                                }}
                                style={{ width: '150px' }}
                            />
                            <Column
                                header="Actions"
                                body={(rowData) => {
                                    if ((rowData.status === 'Failed' || rowData.status === 'Partial Success' || rowData.errorCount > 0)) {
                                        return (
                                            <Authorizer permId={PERMISSIONS.FDE_DOWNLOAD_ERROR_SHEET}>
                                                <Button
                                                    label="Download Errors"
                                                    icon="pi pi-download"
                                                    className="p-button-sm p-button-danger"
                                                    onClick={() => this.downloadErrorSheet(rowData)}
                                                    tooltip="Download Error Sheet"
                                                />
                                            </Authorizer>
                                        );
                                    }
                                    return <span>-</span>;
                                }}
                                style={{ width: '150px' }}
                            />
                        </DataTable>
                    </>
                )}
            </>
        );

        if (showCard) {
            return (
                <>
                    <Toast ref={this.toast} position="bottom-right" />
                    <Card className="transaction-history-card p-mt-3" title={title}>
                        {tableContent}
                    </Card>
                </>
            );
        }

        return (
            <>
                <Toast ref={this.toast} position="bottom-right" />
                {tableContent}
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    orgId: state.currentUserPerms && state.currentUserPerms.userInfo ? state.currentUserPerms.userInfo.orgId : null
});

export default connect(mapStateToProps)(TransactionHistoryTable);

